import { createAction } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "@/app/store";
import { startEditing, undo } from "@/features/configuration/slice";
import { savedShopFor } from "@/features/shops/lib";
import type { Shop } from "@/shared/types/shop";
import type { BillingInfo, LanguageOption, SelectedPlan, UserInfo } from "@/shared/types/user";
import { shellApi } from "./api";
import { selectCurrentConfiguration, selectDisplayedShops } from "./legacySelectors";

export interface BootstrapPayload {
  languages: LanguageOption[];
  billingInfo: BillingInfo;
  user: UserInfo;
  plan: SelectedPlan | unknown[];
  unreadNotifications: number;
  shops: Shop[];
  featureFlags: Record<string, boolean>;
}

/** Dati di sessione caricati: il ponte li copia nello slice legacy e sceglie lo shop iniziale. */
export const bootstrapCompleted = createAction<BootstrapPayload>("shell/bootstrapCompleted");
/** Lo shop corrente e' cambiato: il ponte emette `changed-website` alle viste legacy. */
export const shopChanged = createAction("shell/shopChanged");
/** Un editor Visual Machine ha modifiche: il cambio shop viene delegato all'editor (`before-change-website`). */
export const shopSwitchDelegated = createAction<Shop>("shell/shopSwitchDelegated");
/** Un editor Visual Machine ha modifiche: la navigazione viene delegata all'editor (modale di scarto legacy). */
export const navigationDelegated = createAction<string>("shell/navigationDelegated");

/**
 * Rende `shop` la configurazione corrente, azzera la history, avvisa le viste legacy e rilegge gli shop
 * invalidando il tag `Shop` (websiteClick del legacy: `axios.get(SHOPS).then(setWebsites)`). Vale per ogni
 * cambio applicato (picker, notifica, uscita senza salvare dalla guardia): il picker legacy
 * (`applyWebsite`) non rileggeva gli shop, qui lo fa anche lui per avere un solo percorso.
 */
export const applyShop = (shop: Shop) => (dispatch: AppDispatch) => {
  dispatch(startEditing(shop));
  dispatch(shopChanged());
  dispatch(shellApi.util.invalidateTags(["Shop"]));
};

/** Scarta le modifiche: torna alla versione salvata dello shop corrente (ChangesModal.discardChanges). */
export const discardConfiguration = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  dispatch(
    startEditing({
      ...savedShopFor(selectDisplayedShops(state), selectCurrentConfiguration(state)),
    }),
  );
};

/** La configurazione e' stata salvata: il ponte emette `check-indexing` (con il ritardo di 1 s del legacy). */
export const configurationSaved = createAction("shell/configurationSaved");
/** L'ultima modifica e' stata scartata dalla barra: il ponte emette `discard-changes` alle viste legacy. */
export const configurationDiscarded = createAction("shell/configurationDiscarded");

/** SaveChanges.discardChanges: torna allo snapshot piu' recente della history e lo rimuove. */
export const undoLastChange = () => (dispatch: AppDispatch) => {
  dispatch(undo());
  dispatch(configurationDiscarded());
};
